/**
 * Combat feedback prefs — board motion + discourse popups.
 * Device-local (localStorage). Independent of audio settings.
 */

export type MotionIntensity = "full" | "reduced" | "off";
export type DiscourseLevel = "off" | "minimal" | "full";

export interface CombatFeedbackSettings {
  version: 1;
  /**
   * Board motion: arrows, forks, contest links, action FX.
   * `full` still softens when the OS prefers reduced motion.
   */
  motion: MotionIntensity;
  /**
   * Floating combat text. Default off — board FX owns the fight.
   * minimal = rare sacred / claim lines only.
   */
  discourse: DiscourseLevel;
}

export const COMBAT_FEEDBACK_KEY = "sots-combat-feedback-v1";

export const DEFAULT_COMBAT_FEEDBACK: CombatFeedbackSettings = {
  version: 1,
  motion: "full",
  discourse: "off",
};

export function sanitizeCombatFeedback(
  raw: unknown,
  fallback: CombatFeedbackSettings = DEFAULT_COMBAT_FEEDBACK,
): CombatFeedbackSettings {
  const base = { ...fallback };
  if (!raw || typeof raw !== "object") return base;
  const o = raw as Record<string, unknown>;

  const motion =
    o.motion === "full" || o.motion === "reduced" || o.motion === "off"
      ? o.motion
      : base.motion;
  const discourse =
    o.discourse === "off" ||
    o.discourse === "minimal" ||
    o.discourse === "full"
      ? o.discourse
      : base.discourse;

  return { version: 1, motion, discourse };
}

export function loadCombatFeedback(): CombatFeedbackSettings {
  try {
    if (typeof localStorage === "undefined") {
      return { ...DEFAULT_COMBAT_FEEDBACK };
    }
    const raw = localStorage.getItem(COMBAT_FEEDBACK_KEY);
    if (!raw) return { ...DEFAULT_COMBAT_FEEDBACK };
    return sanitizeCombatFeedback(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_COMBAT_FEEDBACK };
  }
}

export function saveCombatFeedback(settings: CombatFeedbackSettings): void {
  try {
    if (typeof localStorage === "undefined") return;
    const clean = sanitizeCombatFeedback(settings);
    localStorage.setItem(COMBAT_FEEDBACK_KEY, JSON.stringify(clean));
  } catch {
    /* ignore quota / private mode */
  }
}

export function mergeCombatFeedback(
  current: CombatFeedbackSettings,
  partial: Partial<CombatFeedbackSettings>,
): CombatFeedbackSettings {
  return sanitizeCombatFeedback({ ...current, ...partial, version: 1 as const });
}

/** OS prefers reduced motion (browser media query). */
export function systemPrefersReducedMotion(): boolean {
  try {
    return (
      typeof matchMedia !== "undefined" &&
      matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  } catch {
    return false;
  }
}

/**
 * Effective motion after OS preference.
 * User "full" → reduced when OS asks; "reduced" / "off" stay as set.
 */
export function effectiveMotion(
  settings: CombatFeedbackSettings,
  prefersReduced: boolean = systemPrefersReducedMotion(),
): MotionIntensity {
  if (settings.motion === "off") return "off";
  if (settings.motion === "reduced") return "reduced";
  if (prefersReduced) return "reduced";
  return "full";
}

/** What motion layers to draw at a given intensity. */
export type MotionFeatures = {
  advanceArrows: boolean;
  multiplyForks: boolean;
  multiplyTrails: boolean;
  contestLinks: boolean;
  /** All discrete FX (strikes, silence, …) */
  actionFx: boolean;
  /** Only silence / redeem / meet strikes (reduced mode) */
  actionFxCriticalOnly: boolean;
  /** Pulse / imminent animations */
  animate: boolean;
  /** Windup progress bars on tokens */
  windupBars: boolean;
};

export function motionFeatures(intensity: MotionIntensity): MotionFeatures {
  if (intensity === "off") {
    return {
      advanceArrows: false,
      multiplyForks: false,
      multiplyTrails: false,
      contestLinks: false,
      actionFx: false,
      actionFxCriticalOnly: false,
      animate: false,
      windupBars: true, // quiet state still useful
    };
  }
  if (intensity === "reduced") {
    return {
      advanceArrows: true,
      multiplyForks: true,
      multiplyTrails: false,
      contestLinks: false,
      actionFx: true,
      actionFxCriticalOnly: true,
      animate: false,
      windupBars: true,
    };
  }
  return {
    advanceArrows: true,
    multiplyForks: true,
    multiplyTrails: true,
    contestLinks: true,
    actionFx: true,
    actionFxCriticalOnly: false,
    animate: true,
    windupBars: true,
  };
}

/** Whether a discrete FX event is "critical" (shown in reduced motion). */
export function isCriticalBoardFx(type: string, quality?: string): boolean {
  if (type === "silence" || type === "redeem") return true;
  if (type === "boss.arrive" || type === "boss.settle") return true;
  if (type === "strike" && (quality === "meet" || quality === "hit")) return true;
  return false;
}
